import React, { useState, useMemo, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { ScrollText, ClipboardList, Tags, FileUp } from 'lucide-react';
import { useCrud, useApiQuery } from '../../hooks/useApi';
import { usePermissions } from '../../hooks/usePermissions';
import { licenseService, processService, identityService, notificationService } from '../../services/api';
import { suggestLicenseValidade } from '../../utils/licensePrazo';
import { licencaStatusChoices } from '../../utils/licenseStatus';
import toast from 'react-hot-toast';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormSection from '../../components/Common/FormSection';
import Can from '../../components/Common/Can';
import EntityAutocomplete from '../../components/Common/EntityAutocomplete';
import EnterpriseModal from '../../components/Identity/EnterpriseModal';
import ClientModal from '../../components/Identity/ClientModal';
import ProcessoNestedCreateModal from '../../components/Process/ProcessoNestedCreateModal';
import CondicionantesEditor, {
  condicionanteFromApi,
  condicionanteFromPdf,
  syncCondicionantes,
} from '../../components/Licenses/CondicionantesEditor';
import ChecklistEditor, {
  checklistFromApi,
  syncLicenseChecklist,
} from '../../components/Licenses/ChecklistEditor';
import { P } from '../../constants/permissions';
import { unwrapList } from '../../utils/entityIds';
import { userFacingError } from '../../i18n/userFacingError';
import { formatCnpj, persistCompanyCnpj } from '../../utils/cnpj';
import { useAuth } from '../../contexts/AuthContext';
import { isRequesterRole, partyNoun } from '../../utils/tenantProfile';
import { useTenant } from '../../contexts/TenantContext';
import { isOwnedEnterprise } from '../../utils/portalScope';
import {
  documentDigitsFromParsed,
  ensureEmpreendimentoFromPdf,
  ensureRequerenteFromPdf,
} from '../../utils/licencaPdfImportEntities';
import {
  activityHintsFromPdf,
  findTipoLicencaByTipoEAtividade,
} from '../../utils/matchTipoLicenca';

const LICENSE_NESTED_MODAL_Z = 'z-[60]';

function onlyDigits(s) {
  return String(s || '').replace(/\D/g, '');
}
function normProc(s) {
  return String(s || '')
    .replace(/\s/g, '')
    .toLowerCase();
}
function htmlToPlainText(html) {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return String(html)
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .trim();
  }
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
}

function normalizeForMatch(s) {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function findEmpreendimentoIdByPdfNome(enterprises, nome) {
  const n = normalizeForMatch(nome);
  if (n.length < 2) return { id: null, kind: 'none' };
  const list = Array.isArray(enterprises) ? enterprises : [];
  const ex = list.find((e) => normalizeForMatch(e.name || e.nome) === n);
  if (ex) return { id: ex.id, kind: 'exact' };
  const sub = list.find((e) => {
    const en = normalizeForMatch(e.name || e.nome);
    return en && (en.includes(n) || n.includes(en));
  });
  if (sub) return { id: sub.id, kind: 'contains' };
  const words = n.split(' ').filter((w) => w.length > 1);
  let best = null;
  let bestS = 0;
  for (const e of list) {
    const en = normalizeForMatch(e.name || e.nome);
    if (!en) continue;
    const s = words.filter((w) => w.length > 1 && en.includes(w)).length;
    if (s > bestS) {
      bestS = s;
      best = e;
    }
  }
  if (best && (bestS >= 2 || (bestS === 1 && words.length === 1))) {
    return { id: best.id, kind: 'fuzzy' };
  }
  return { id: null, kind: 'none' };
}

/**
 * Formulário completo (modal) de licença + import PDF + modais aninhados.
 * Usar apenas embarcado em /licenses; a rota /license/licencas redireciona para /licenses.
 * @param {{ onSaved?: () => void }} props
 */
const LicencasForm = forwardRef(function LicencasForm({ onSaved }, ref) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  /** Modais de cadastro rápido empilhados sobre o CRUD de licença */
  const [childModal, setChildModal] = useState(null);
  const [nestedTipoNome, setNestedTipoNome] = useState('');
  const [savingNestedTipo, setSavingNestedTipo] = useState(false);
  const pdfImportInputRef = useRef(null);
  const [importingPdf, setImportingPdf] = useState(false);
  /** PDF já gravado na central de arquivos durante a importação. */
  const [pdfArquivoImportado, setPdfArquivoImportado] = useState(null);
  /** Condicionantes extraídas do PDF — mescladas ao rascunho ao importar. */
  const [pendingCondicionantesFromPdf, setPendingCondicionantesFromPdf] = useState(null);
  /** Rascunho de condicionantes (manual + PDF) para criar/editar junto com a licença. */
  const [condicionantesDraft, setCondicionantesDraft] = useState([]);
  const [checklistDraft, setChecklistDraft] = useState([]);
  const [licensePrazoDays, setLicensePrazoDays] = useState(null);
  const [validadeHint, setValidadeHint] = useState('');
  const [form, setForm] = useState({
    tipo_licenca_id: '',
    processo_id: '',
    requerente_id: '',
    data_abertura_processo: new Date().toISOString().slice(0, 10),
    status: '1',
    numero: '',
    empreendimento_id: '',
    validade: '',
    data_prorrogacao: '',
    observacao: '',
    publicacao_status: 'rascunho',
    dou_url: '',
  });
  const [assinaturas, setAssinaturas] = useState([]);
  const [signatario, setSignatario] = useState('');
  const [signatarioCpf, setSignatarioCpf] = useState('');
  const [assinando, setAssinando] = useState(false);

  const loadAssinaturas = useCallback(async (licencaId) => {
    if (!licencaId) {
      setAssinaturas([]);
      return;
    }
    try {
      const r = await licenseService.listAssinaturas(licencaId);
      const raw = r.data;
      setAssinaturas(Array.isArray(raw) ? raw : raw?.data ?? []);
    } catch {
      setAssinaturas([]);
    }
  }, []);

  const { useList, useUpdate } = useCrud('licencas', licenseService.licencaCrud);
  const { useList: useTiposList } = useCrud('tipos-licenca', licenseService.tipoLicencaCrud);
  const { useList: useProcessosList } = useCrud('processos', processService.processoCrud);

  const { data: listData, error } = useList();
  const { data: tiposData } = useTiposList();
  const { data: processosData } = useProcessosList();
  const tiposLicenca = unwrapList(tiposData);
  const processos = unwrapList(processosData);

  const { data: clientsPayload } = useApiQuery(
    'identity-clients-licencas-modal',
    () => identityService.getClients({ limit: 1000 }).then((r) => r.data)
  );
  const clients = unwrapList(clientsPayload);

  const { data: enterprisesPayload } = useApiQuery(
    'identity-enterprises-licencas-modal',
    () => identityService.getEnterprises({ limit: 1000 }).then((r) => r.data)
  );
  const enterprises = useMemo(() => {
    const rows = unwrapList(enterprisesPayload);
    if (!isRequesterRole(user)) return rows;
    return rows.filter((row) => isOwnedEnterprise(row, user));
  }, [enterprisesPayload, user]);

  const { data: companiesPayload } = useApiQuery(
    'identity-companies-licencas-modal',
    () => identityService.getCompanies({ limit: 1000 }).then((r) => r.data)
  );
  const companies = unwrapList(companiesPayload);

  const tipoOptions = useMemo(
    () => tiposLicenca.map((t) => ({ id: t.id, label: t.nome || t.name || t.id })),
    [tiposLicenca]
  );
  const processoOptions = useMemo(
    () =>
      processos.map((p) => {
        const sid = p.id != null ? String(p.id) : '';
        return {
          id: p.id,
          label: p.numero
            ? `${p.numero}${sid ? ` (${sid.slice(0, 8)}…)` : ''}`
            : sid
              ? `Processo ${sid.slice(0, 8)}…`
              : '—',
        };
      }),
    [processos]
  );
  const clientOptions = useMemo(
    () => {
      const companyById = Object.fromEntries(
        companies.map((co) => [String(co.id || ''), co]),
      );
      return clients.map((c) => {
        const company = companyById[String(c.company_id || '')];
        const doc = c.document || c.cpf || c.cnpj || company?.cnpj || '';
        const base = c.name || company?.name || '—';
        return { id: c.id, label: doc ? `${base} · ${doc}` : base };
      });
    },
    [clients, companies]
  );
  const enterpriseOptions = useMemo(
    () => enterprises.map((e) => ({ id: e.id, label: e.name || e.nome || e.id })),
    [enterprises]
  );

  const items = unwrapList(listData);

  const updateMutation = useUpdate();

  useEffect(() => {
    let active = true;
    notificationService
      .getRules()
      .then(({ data }) => {
        if (active) setLicensePrazoDays(data?.rules?.license_prazo_days || null);
      })
      .catch(() => {
        if (active) setLicensePrazoDays(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const applySuggestedValidade = useCallback(
    (nextForm, { overwrite = false } = {}) => {
      if (!overwrite && nextForm.validade) {
        setValidadeHint('');
        return nextForm;
      }
      const tipo = tiposLicenca.find((t) => t.id === nextForm.tipo_licenca_id);
      if (!tipo || !nextForm.data_abertura_processo) {
        setValidadeHint('');
        return nextForm;
      }
      const suggested = suggestLicenseValidade(
        nextForm.data_abertura_processo,
        tipo.nome || tipo.name,
        licensePrazoDays,
      );
      if (!suggested.validade) return nextForm;
      setValidadeHint(
        `Sugerido: ${suggested.days} dias (${suggested.kind}) a partir da abertura. Ajuste se necessário.`,
      );
      return { ...nextForm, validade: suggested.validade };
    },
    [licensePrazoDays, tiposLicenca],
  );

  const handleCreate = useCallback((prefill = {}) => {
    setEditingItem(null);
    const next = {
      tipo_licenca_id: prefill.tipo_licenca_id || '',
      processo_id: prefill.processo_id || '',
      requerente_id: prefill.requerente_id || '',
      data_abertura_processo: prefill.data_abertura_processo || new Date().toISOString().slice(0, 10),
      status: '1',
      numero: '',
      empreendimento_id: '',
      validade: '',
      data_prorrogacao: '',
      observacao: '',
      publicacao_status: 'rascunho',
      dou_url: '',
    };
    setForm(applySuggestedValidade(next));
    setPendingCondicionantesFromPdf(null);
    setPdfArquivoImportado(null);
    setCondicionantesDraft([]);
    setAssinaturas([]);
    setSignatario('');
    setSignatarioCpf('');
    setShowModal(true);
  }, [applySuggestedValidade]);

  const loadCondicionantesForEdit = useCallback(async (licencaId) => {
    try {
      const r = await licenseService.getCondicionantesByLicenca(licencaId);
      const raw = r.data;
      const list = Array.isArray(raw) ? raw : raw?.data ?? [];
      setCondicionantesDraft(list.map(condicionanteFromApi));
    } catch {
      setCondicionantesDraft([]);
    }
  }, []);

  const loadChecklistForLicenca = useCallback(async (licencaId) => {
    try {
      const r = await licenseService.listLicencaChecklist(licencaId);
      const list = Array.isArray(r.data) ? r.data : [];
      setChecklistDraft(list.map(checklistFromApi));
    } catch {
      setChecklistDraft([]);
    }
  }, []);

  const loadChecklistFromTipo = useCallback(async (tipoId) => {
    if (!tipoId) {
      setChecklistDraft([]);
      return;
    }
    try {
      const r = await licenseService.listTipoChecklist(tipoId);
      const list = Array.isArray(r.data) ? r.data : [];
      setChecklistDraft(
        list.map((item) => ({
          localKey: item.id,
          descricao: item.descricao,
          tipo_checklist: item.tipo_checklist || 'basico',
          obrigatorio: Boolean(item.obrigatorio),
          cumprido: false,
          pendingFiles: [],
        }))
      );
    } catch {
      setChecklistDraft([]);
    }
  }, []);

  const handleEdit = useCallback((item) => {
    setEditingItem(item);
    setPdfArquivoImportado(null);
    setForm({
      tipo_licenca_id: item.tipo_licenca_id || '',
      processo_id: item.processo_id || '',
      requerente_id: item.requerente_id || '',
      data_abertura_processo: (item.data_abertura_processo || '').slice(0, 10),
      status: item.status || '1',
      numero: item.numero || '',
      empreendimento_id: item.empreendimento_id || '',
      validade: (item.validade || '').slice(0, 10) || '',
      data_prorrogacao: (item.data_prorrogacao || '').slice(0, 10) || '',
      observacao: item.observacao || '',
      publicacao_status: item.publicacao_status || 'rascunho',
      dou_url: item.dou_url || '',
    });
    setPendingCondicionantesFromPdf(null);
    setCondicionantesDraft([]);
    setChecklistDraft([]);
    setShowModal(true);
    if (item?.id) {
      loadCondicionantesForEdit(item.id);
      loadChecklistForLicenca(item.id);
      loadAssinaturas(item.id);
    }
  }, [loadCondicionantesForEdit, loadChecklistForLicenca, loadAssinaturas]);

  const openEditById = useCallback(
    async (rawId) => {
      const sid = String(rawId);
      let item = items.find((x) => String(x.id) === sid);
      if (!item) {
        try {
          item = await licenseService.licencaCrud.getItem(sid);
        } catch {
          toast.error('Não foi possível carregar a licença.');
          return;
        }
      }
      if (item) {
        handleEdit(item);
      } else {
        toast.error('Licença não encontrada.');
      }
    },
    [items, handleEdit]
  );

  useImperativeHandle(
    ref,
    () => ({
      openCreate: (prefill) => {
        handleCreate(prefill);
      },
      openEditById: (id) => {
        openEditById(id);
      },
    }),
    [handleCreate, openEditById]
  );

  useEffect(() => {
    if (!location.state?.openCreate) return;
    navigate(location.pathname, { replace: true, state: {} });
    handleCreate();
  }, [location.state, location.pathname, navigate, handleCreate]);

  useEffect(() => {
    const openId = location.state?.openEditId;
    if (!openId) return;
    navigate(location.pathname, { replace: true, state: {} });
    (async () => {
      const sid = String(openId);
      let item = items.find((x) => String(x.id) === sid);
      if (!item) {
        try {
          item = await licenseService.licencaCrud.getItem(sid);
        } catch {
          toast.error('Licença não encontrada ou sem permissão.');
          return;
        }
      }
      if (item) {
        setEditingItem(item);
        setForm({
          tipo_licenca_id: item.tipo_licenca_id || '',
          processo_id: item.processo_id || '',
          requerente_id: item.requerente_id || '',
          data_abertura_processo:
            (item.data_abertura_processo || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
          status: item.status || '1',
          numero: item.numero || '',
          empreendimento_id: item.empreendimento_id || '',
          validade: (item.validade || '').slice(0, 10) || '',
          data_prorrogacao: (item.data_prorrogacao || '').slice(0, 10) || '',
          observacao: item.observacao || '',
          publicacao_status: item.publicacao_status || 'rascunho',
          dou_url: item.dou_url || '',
        });
        setPendingCondicionantesFromPdf(null);
        setCondicionantesDraft([]);
        setShowModal(true);
        if (item.id) loadCondicionantesForEdit(item.id);
      } else {
        toast.error('Licença não encontrada.');
      }
    })();
  }, [location.state, items, location.pathname, navigate, loadCondicionantesForEdit]);

  const requesterUser = isRequesterRole(user);
  const canAddTipoLicenca = !requesterUser && (hasPermission(P.LICENSE_TIPOS) || hasPermission(P.LICENSE_LICENCAS));
  const canAddProcesso = !requesterUser && (hasPermission(P.PROCESS_ACCESS) || hasPermission(P.LICENSE_LICENCAS));
  const canAddCliente = !requesterUser && (hasPermission(P.IDENTITY_ACCESS) || hasPermission(P.LICENSE_LICENCAS));
  const canAddEmpreendimento =
    !requesterUser &&
    (hasPermission(P.ENTERPRISES_VIEW) || hasPermission(P.IDENTITY_ACCESS) || hasPermission(P.LICENSE_LICENCAS));

  const openNestedTipo = () => {
    setNestedTipoNome('');
    setChildModal('tipo');
  };
  const handleNestedTipoSave = async (e) => {
    e.preventDefault();
    const nome = nestedTipoNome.trim();
    if (!nome) {
      toast.error('Informe o nome do tipo de licença');
      return;
    }
    setSavingNestedTipo(true);
    try {
      const created = await licenseService.tipoLicencaCrud.create({ nome });
      const id = created?.id;
      if (!id) {
        toast.error('Tipo criado, mas a resposta não trouxe o identificador.');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['tipos-licenca'] });
      setForm((f) => ({ ...f, tipo_licenca_id: id }));
      setChildModal(null);
      toast.success('Tipo de licença cadastrado e selecionado');
    } catch (err) {
      toast.error(userFacingError(err, 'licenses:typeCreateError'));
    } finally {
      setSavingNestedTipo(false);
    }
  };

  const handleNestedEnterpriseSave = async (data) => {
    try {
      if (!data.inviteEmail?.trim()) {
        toast.error('Informe o e-mail para criar o acesso do empreendimento.');
        return;
      }
      const res = await identityService.createEnterpriseWithAccessInvite({
        enterprise: {
          name: data.name,
          company_id: data.company_id,
          cnpj: data.cnpj || undefined,
          description: data.description || null,
          address: data.address || null,
          city: data.city || null,
          state: data.state || null,
          zip_code: data.zip_code || null,
          latitude: data.latitude,
          longitude: data.longitude,
          area_geojson: data.area_geojson,
        },
        invite_email: data.inviteEmail.trim(),
        login_with: 'email',
      });
      await persistCompanyCnpj(data.company_id, data.cnpj, identityService);
      const created = res.data?.enterprise || res.data;
      const id = created?.id;
      if (!id) {
        toast.error('Empreendimento criado, mas a resposta não trouxe o identificador.');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['identity-enterprises-licencas-modal'] });
      setForm((f) => ({ ...f, empreendimento_id: id }));
      setChildModal(null);
      toast.success('Empreendimento cadastrado e selecionado');
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    }
  };

  const handleNestedClientSave = async (clientData) => {
    try {
      const res = await identityService.createClient(clientData);
      const body = res.data;
      const id = body?.id ?? body?.data?.id;
      if (!id) {
        toast.error(`${party.One} criado, mas a resposta não trouxe o identificador.`);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['identity-clients-licencas-modal'] });
      setForm((f) => ({ ...f, requerente_id: id }));
      setChildModal(null);
      toast.success(`${party.One} cadastrado e selecionado`);
    } catch (e) {
      toast.error(userFacingError(e, 'identity:clientCreateError'));
    }
  };

  const buildLicencaPayload = () => {
    const base = {
      tipo_licenca_id: form.tipo_licenca_id,
      requerente_id: form.requerente_id,
      data_abertura_processo: form.data_abertura_processo,
      status: form.status,
      gestao: '1',
      processo_licenciamento: false,
      notificado: false,
    };
    if (form.processo_id) base.processo_id = form.processo_id;
    else delete base.processo_id;
    if (form.numero?.trim()) base.numero = form.numero.trim();
    if (form.empreendimento_id) base.empreendimento_id = form.empreendimento_id;
    if (form.validade) base.validade = form.validade;
    if (form.data_prorrogacao) base.data_prorrogacao = form.data_prorrogacao;
    if (form.observacao?.trim()) base.observacao = form.observacao.trim();
    if (form.publicacao_status) base.publicacao_status = form.publicacao_status;
    if (form.dou_url?.trim()) base.dou_url = form.dou_url.trim();
    return base;
  };

  const handlePdfFileChange = async (e) => {
    const f = e.target?.files?.[0];
    if (e?.target) e.target.value = '';
    if (!f) return;
    if (!f.name?.toLowerCase().endsWith('.pdf')) {
      toast.error('Selecione um arquivo PDF.');
      return;
    }
    setImportingPdf(true);
    try {
      const { data: d } = await licenseService.importLicencaFromPdf(f);
      if (d.arquivo_id) {
        setPdfArquivoImportado({
          id: d.arquivo_id,
          nome: d.arquivo_nome || f.name,
          url: d.arquivo_url || null,
          vinculado: Boolean(d.anexo_vinculado),
        });
      }
      if (d.erro) {
        toast.error(d.erro);
        return;
      }
      if (d.licenca_existente_id) {
        let item = d.licenca_existente;
        if (!item?.id) {
          item = (await licenseService.getLicenca(d.licenca_existente_id)).data;
        }
        await queryClient.invalidateQueries({ queryKey: ['licencas'] });
        await queryClient.invalidateQueries({ queryKey: ['identity-clients-licencas-modal'] });
        await queryClient.invalidateQueries({ queryKey: ['identity-enterprises-licencas-modal'] });
        if (item?.id) {
          handleEdit(item);
          setPdfArquivoImportado({
            id: d.arquivo_id,
            nome: d.arquivo_nome || f.name,
            url: d.arquivo_url || null,
            vinculado: Boolean(d.anexo_vinculado),
          });
          const campos = Array.isArray(d.licenca_campos_completados) ? d.licenca_campos_completados.length : 0;
          const conds = Number(d.condicionantes_incluidas) || 0;
          toast.success(
            campos || conds
              ? `Licença ${item.numero || d.numero_licenca || ''} já cadastrada. Dados faltantes completados e PDF vinculado.`
              : `Licença ${item.numero || d.numero_licenca || ''} já cadastrada. PDF vinculado ao registro.`
          );
        }
        return;
      }
      const procStr = (d.processo_administrativo || '').trim();
      const procNumNorm = normProc(procStr);
      const procMatch =
        procStr &&
        processos.find((x) => {
          const n = (x.numero && String(x.numero).trim()) || '';
          if (!n) return false;
          const nn = normProc(n);
          return nn === procNumNorm || nn.includes(procNumNorm) || procNumNorm.includes(nn);
        });
      const cj = documentDigitsFromParsed(d);
      const atividadeHints = activityHintsFromPdf(d);
      let clientMatch = d.requerente_id ? { id: d.requerente_id } : null;
      let companyId = d.company_id || null;
      let personId = d.person_id || null;
      let createdClient = Boolean(d.requerente_criado);
      if (d.requerente_id) {
        await queryClient.invalidateQueries({ queryKey: ['identity-clients-licencas-modal'] });
        await queryClient.invalidateQueries({ queryKey: ['identity-companies-licencas-modal'] });
      }
      if (clientMatch && createdClient) {
        toast.success('Requerente cadastrado a partir do PDF (sem usuário de acesso).');
      }
      let tipoMatch = d.tipo_licenca_sugerido
        ? findTipoLicencaByTipoEAtividade(tiposLicenca, d.tipo_licenca_sugerido, atividadeHints)
        : { id: null, kind: 'none', composed: d.tipo_licenca_sugerido };
      if (tipoMatch?.id) {
        toast(`Tipo vinculado: ${tipoMatch.nome}`, { icon: 'ℹ️' });
      }
      if (!clientMatch) {
        clientMatch =
          cj && clients.find((x) => onlyDigits(x.cnpj || x.document || x.cpf) === cj);
        companyId = clientMatch?.company_id || companyId;
        personId = clientMatch?.person_id || personId;
      }
      if (!clientMatch && cj) {
        try {
          const ensured = await ensureRequerenteFromPdf({
            parsed: d,
            clients,
            identityService,
          });
          if (ensured.id) {
            clientMatch = { id: ensured.id };
            createdClient = ensured.created;
            companyId = ensured.companyId;
            personId = ensured.personId;
            await queryClient.invalidateQueries({ queryKey: ['identity-clients-licencas-modal'] });
            await queryClient.invalidateQueries({ queryKey: ['identity-companies-licencas-modal'] });
            if (createdClient) {
              toast.success('Requerente cadastrado a partir do PDF (sem usuário de acesso).');
            }
          }
        } catch (err) {
          toast.error(userFacingError(err, 'identity:clientCreateError'));
        }
      }
      if (!companyId && cj && String(cj).length === 14) {
        try {
          const company = (await identityService.getCompanyByCnpj(formatCnpj(cj))).data;
          companyId = company?.id || companyId;
        } catch {
          /* empresa ainda não existe ou 404 */
        }
      }
      let entMatch = d.empreendimento_id
        ? { id: d.empreendimento_id, kind: d.empreendimento_criado ? 'created' : 'exact' }
        : d.empreendimento_nome
          ? findEmpreendimentoIdByPdfNome(enterprises, d.empreendimento_nome)
          : { id: null, kind: 'none' };
      if (entMatch?.id && d.empreendimento_criado) {
        toast.success('Empreendimento cadastrado a partir do PDF, com as coordenadas.');
        await queryClient.invalidateQueries({ queryKey: ['identity-enterprises-licencas-modal'] });
      }
      if ((!entMatch?.id || d.latitude != null) && !d.empreendimento_id) {
        try {
          const ensuredEnt = await ensureEmpreendimentoFromPdf({
            parsed: d,
            enterprises,
            identityService,
            companyId,
            personId,
          });
          if (ensuredEnt.id) {
            entMatch = { id: ensuredEnt.id, kind: ensuredEnt.created ? 'created' : entMatch.kind || 'exact' };
            await queryClient.invalidateQueries({ queryKey: ['identity-enterprises-licencas-modal'] });
            if (ensuredEnt.created) {
              toast.success('Empreendimento cadastrado a partir do PDF, com as coordenadas.');
            } else if (ensuredEnt.updatedCoords) {
              toast.success('Coordenadas do PDF aplicadas ao empreendimento.');
            }
          }
        } catch (err) {
          toast.error(userFacingError(err, 'saveError'));
        }
      }
      setForm((prev) => ({
        ...prev,
        tipo_licenca_id: tipoMatch?.id != null ? tipoMatch.id : prev.tipo_licenca_id,
        processo_id: procMatch?.id != null ? procMatch.id : prev.processo_id,
        requerente_id: clientMatch?.id != null ? clientMatch.id : prev.requerente_id,
        empreendimento_id: entMatch?.id != null ? entMatch.id : prev.empreendimento_id,
        numero: d.numero_licenca
          ? String(d.numero_licenca)
          : d.documento_licenca_id
            ? String(d.documento_licenca_id)
            : prev.numero,
        data_abertura_processo: d.validade_inicio
          ? d.validade_inicio.slice(0, 10)
          : d.data_emissao
            ? d.data_emissao.slice(0, 10)
            : prev.data_abertura_processo,
        validade: d.validade_fim ? d.validade_fim.slice(0, 10) : prev.validade,
        data_prorrogacao: d.data_prorrogacao ? d.data_prorrogacao.slice(0, 10) : prev.data_prorrogacao,
        observacao: d.observacao_sugerida
          ? htmlToPlainText(d.observacao_sugerida)
          : prev.observacao,
        status: d.validade_fim || d.data_prorrogacao ? '3' : prev.status,
      }));
      setPendingCondicionantesFromPdf(
        Array.isArray(d.condicionantes) && d.condicionantes.length > 0 ? d.condicionantes : null
      );
      if (Array.isArray(d.condicionantes) && d.condicionantes.length > 0) {
        setCondicionantesDraft(d.condicionantes.map((c, i) => condicionanteFromPdf(c, i)));
        requestAnimationFrame(() => {
          document.getElementById('lic-sec-cond')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
      if (d.avisos?.length) {
        toast(d.avisos.join(' '), { icon: 'ℹ️', duration: 6000 });
      }
      const n = d.condicionantes?.length || 0;
      toast.success(
        n
          ? `PDF importado e salvo. ${n} condicionante(s) na seção abaixo. Ao salvar, o arquivo fica vinculado à licença.`
          : 'PDF importado e salvo. Revise os campos; ao salvar, o arquivo fica vinculado à licença.'
      );
      if (procStr && !procMatch) {
        toast('Processo do PDF não está na lista — a licença pode ser salva sem vínculo.', {
          icon: 'ℹ️',
        });
      }
      if (cj && !clientMatch) {
        toast('Não foi possível cadastrar o requerente automaticamente — selecione manualmente.', {
          icon: '⚠️',
        });
      }
      if (d.tipo_licenca_sugerido && !tipoMatch?.id) {
        toast(
          `Tipo no PDF: "${d.tipo_licenca_sugerido}" — selecione o tipo cadastrado que melhor se adequa à atividade.`,
          { icon: '⚠️', duration: 7000 }
        );
      }
      if (d.empreendimento_nome && !entMatch?.id) {
        toast(
          `Empreendimento no PDF: "${d.empreendimento_nome}" — não foi possível cadastrar; selecione ou cadastre.`,
          { icon: '⚠️', duration: 7000 }
        );
      }
    } catch (err) {
      toast.error(userFacingError(err, 'licenses:importPdfError'));
    } finally {
      setImportingPdf(false);
    }
  };

  const linkImportedPdf = async (licencaId) => {
    if (!licencaId || !pdfArquivoImportado?.id || pdfArquivoImportado.vinculado) return;
    try {
      await licenseService.vincularArquivoLicenca(licencaId, pdfArquivoImportado.id);
      setPdfArquivoImportado((current) => (current ? { ...current, vinculado: true } : current));
    } catch (err) {
      toast.error(userFacingError(err, 'licenses:importPdfError'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tipo_licenca_id || !form.requerente_id) {
      toast.error('Preencha tipo de licença e requerente');
      return;
    }
    const payload = buildLicencaPayload();
    if (payload.publicacao_status === 'publicado' && assinaturas.length < 1) {
      toast.error('Publique só após registrar ao menos uma assinatura da licença.');
      return;
    }
    if (editingItem) {
      try {
        const currentStatus = String(editingItem.status ?? '');
        const nextStatus = payload.status != null ? String(payload.status) : currentStatus;
        const { status, ...rest } = payload;
        if (nextStatus && nextStatus !== currentStatus) {
          await licenseService.transitionLicenca(editingItem.id, nextStatus);
        }
        await updateMutation.mutateAsync({ id: editingItem.id, data: rest });
        await syncCondicionantes(editingItem.id, condicionantesDraft);
        await syncLicenseChecklist(editingItem.id, checklistDraft);
        await linkImportedPdf(editingItem.id);
        toast.success('Licença atualizada');
        setShowModal(false);
        setEditingItem(null);
        setChildModal(null);
        setCondicionantesDraft([]);
        await queryClient.invalidateQueries({ queryKey: ['licencas'] });
        onSaved?.();
      } catch (err) {
        toast.error(userFacingError(err, 'updateError'));
      }
    } else {
      try {
        if (payload.processo_id) {
          try {
            const found = await licenseService.getLicencaByProcesso(payload.processo_id);
            if (found?.data?.id) {
              toast.error('Já existe uma licença vinculada a este processo.');
              return;
            }
          } catch (lookupErr) {
            if (lookupErr?.response?.status !== 404) throw lookupErr;
          }
        }
        const created = (await licenseService.createLicenca(payload))?.data;
        const licId = created?.id;
        if (licId) await linkImportedPdf(licId);
        if (licId && condicionantesDraft.length) {
          await syncCondicionantes(licId, condicionantesDraft);
          const n = condicionantesDraft.filter((c) => !c._deleted).length;
          if (n) toast.success(`Incluídas ${n} condicionante(s).`);
        }
        if (licId && checklistDraft.length) {
          await syncLicenseChecklist(licId, checklistDraft);
        }
        setPendingCondicionantesFromPdf(null);
        setCondicionantesDraft([]);
        setChecklistDraft([]);
        setShowModal(false);
        setChildModal(null);
        await queryClient.invalidateQueries({ queryKey: ['licencas'] });
        onSaved?.();
      } catch (err) {
        toast.error(userFacingError(err, 'createError'));
      }
    }
  };

  if (error) {
    return null;
  }

  return (
    <>
      {showModal && (
        <FormModalLayout
          title={editingItem ? 'Editar licença' : 'Nova licença'}
          subtitle="Associe tipo de licença, processo administrativo, requerente e, se houver, empreendimento."
          headerIcon={ScrollText}
          historyEntityType="licenses"
          historyEntityId={editingItem?.id}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
            setChildModal(null);
            setPendingCondicionantesFromPdf(null);
            setCondicionantesDraft([]);
          }}
          size="4xl"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                  setChildModal(null);
                  setPendingCondicionantesFromPdf(null);
                  setPdfArquivoImportado(null);
                  setCondicionantesDraft([]);
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.LICENSE_LICENCAS}>
                <button type="submit" form="licenca-crud-form" className="btn btn-primary">
                  {editingItem ? 'Salvar' : 'Criar'}
                </button>
              </Can>
            </>
          }
        >
          <form id="licenca-crud-form" onSubmit={handleSubmit} className="space-y-6">
            <input
              ref={pdfImportInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={handlePdfFileChange}
            />
            <FormSection
              id="lic-sec-vinculos"
              title="Vínculos obrigatórios"
              description="Busque pelo nome ou número; o sistema grava o identificador interno automaticamente."
              icon={ScrollText}
              variant="primary"
            >
              <div className="form-group">
                <EntityAutocomplete
                  id="lic-tipo"
                  label="Tipo de licença"
                  required
                  value={form.tipo_licenca_id}
                  onChange={(id) => {
                    setForm((f) => applySuggestedValidade({ ...f, tipo_licenca_id: id }));
                    if (!editingItem) loadChecklistFromTipo(id);
                  }}
                  options={tipoOptions}
                  placeholder="Buscar tipo de licença…"
                  onAddNew={canAddTipoLicenca ? openNestedTipo : undefined}
                  addNewLabel="Novo tipo de licença…"
                />
              </div>
              <div className="form-group">
                <EntityAutocomplete
                  id="lic-proc"
                  label="Processo administrativo (opcional)"
                  value={form.processo_id}
                  onChange={(id) => setForm((f) => ({ ...f, processo_id: id }))}
                  options={processoOptions}
                  placeholder="Buscar pelo número do processo…"
                  onAddNew={canAddProcesso ? () => setChildModal('processo') : undefined}
                  addNewLabel="Novo processo administrativo…"
                />
              </div>
              <div className="form-group">
                <EntityAutocomplete
                  id="lic-req"
                  label={party.One}
                  required
                  value={form.requerente_id}
                  onChange={(id) => setForm((f) => ({ ...f, requerente_id: id }))}
                  options={clientOptions}
                  placeholder={`Buscar ${party.one} por nome ou documento…`}
                  onAddNew={canAddCliente ? () => setChildModal('cliente') : undefined}
                  addNewLabel={`Novo ${party.one}…`}
                />
              </div>
            </FormSection>
            <FormSection
              id="lic-sec-registro"
              title="Dados do registro"
              description="Datas, status e identificadores complementares."
              icon={ClipboardList}
              variant="slate"
            >
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <button
                  type="button"
                  disabled={importingPdf}
                  onClick={() => pdfImportInputRef.current?.click()}
                  className="btn btn-outline inline-flex w-fit items-center gap-2"
                >
                  <FileUp className="h-4 w-4" />
                  {importingPdf ? 'Lendo PDF…' : 'Importar de PDF'}
                </button>
                {pdfArquivoImportado?.nome ? (
                  <span className="text-sm text-slate-600">
                    PDF salvo: {pdfArquivoImportado.nome}
                    {pdfArquivoImportado.vinculado
                      ? ' — vinculado à licença.'
                      : ' — será vinculado ao salvar.'}
                  </span>
                ) : null}
                <p className="text-sm text-slate-600">
                  Lê o texto do PDF (IPLAM, SEMMA e similares). Cadastra requerente, empreendimento e tipo se não existirem, sem criar usuário de acesso. PDF só-imagem passa por OCR quando o Tesseract está disponível.
                </p>
                {condicionantesDraft.filter((c) => !c._deleted).length > 0 && (
                  <span className="text-sm text-primary-700">
                    {condicionantesDraft.filter((c) => !c._deleted).length} condicionante(s) no rascunho.
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="lic-data" className="form-label form-label-required">
                    Data de abertura
                  </label>
                  <input
                    id="lic-data"
                    type="date"
                    value={form.data_abertura_processo}
                    onChange={(e) =>
                      setForm((f) => applySuggestedValidade({ ...f, data_abertura_processo: e.target.value }))
                    }
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lic-status" className="form-label">
                    Status
                  </label>
                  <select
                    id="lic-status"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className="form-select"
                  >
                    {licencaStatusChoices(editingItem?.status, { creating: !editingItem }).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group sm:col-span-2">
                  <label htmlFor="lic-numero" className="form-label">
                    Número
                  </label>
                  <input
                    id="lic-numero"
                    type="text"
                    value={form.numero}
                    onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))}
                    className="form-input max-w-md"
                    placeholder="Ex: 2024/001"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lic-validade" className="form-label">
                    Validade
                  </label>
                  <input
                    id="lic-validade"
                    type="date"
                    value={form.validade}
                    onChange={(e) => {
                      setValidadeHint('');
                      setForm((f) => ({ ...f, validade: e.target.value }));
                    }}
                    className="form-input"
                  />
                  {validadeHint && !editingItem ? (
                    <p className="mt-1 text-xs text-slate-600">{validadeHint}</p>
                  ) : null}
                </div>
                <div className="form-group">
                  <label htmlFor="lic-prorrogacao" className="form-label">
                    Data de prorrogação
                  </label>
                  <input
                    id="lic-prorrogacao"
                    type="date"
                    value={form.data_prorrogacao}
                    onChange={(e) => setForm((f) => ({ ...f, data_prorrogacao: e.target.value }))}
                    className="form-input"
                  />
                </div>
                <div className="form-group sm:col-span-2">
                  <label htmlFor="lic-obs" className="form-label">
                    Observação
                  </label>
                  <textarea
                    id="lic-obs"
                    rows={4}
                    value={form.observacao}
                    onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))}
                    className="form-input font-sans"
                    placeholder="Resumo do PDF ou anotações internas"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lic-pub-status" className="form-label">
                    Publicação no Diário Oficial
                  </label>
                  <select
                    id="lic-pub-status"
                    className="form-select"
                    value={form.publicacao_status || 'rascunho'}
                    onChange={(e) => setForm((f) => ({ ...f, publicacao_status: e.target.value }))}
                  >
                    <option value="rascunho">Rascunho</option>
                    <option value="pronto_publicar">Pronta para publicar</option>
                    <option value="publicado">Publicada</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="lic-dou" className="form-label">
                    URL do DOU / DOE
                  </label>
                  <input
                    id="lic-dou"
                    className="form-input"
                    value={form.dou_url || ''}
                    onChange={(e) => setForm((f) => ({ ...f, dou_url: e.target.value }))}
                    placeholder="https://www.in.gov.br/..."
                  />
                </div>
                {editingItem?.id ? (
                  <div className="form-group sm:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <p className="text-sm font-medium text-gray-800">Assinatura da licença</p>
                    <p className="text-xs text-gray-500">
                      O hash SHA-256 é calculado sobre o PDF concatenado. Sem certificado A1 no servidor,
                      fica o registro do responsável. Publicar no DO exige ao menos uma assinatura.
                    </p>
                    {assinaturas.length ? (
                      <ul className="space-y-1 text-xs text-gray-700">
                        {assinaturas.map((a) => (
                          <li key={a.id}>
                            {a.signatario}
                            {a.cpf ? ` · ${a.cpf}` : ''} · {a.tipo}/{a.status}
                            {a.created_at ? ` · ${new Date(a.created_at).toLocaleString('pt-BR')}` : ''}
                            <br />
                            <span className="font-mono break-all">SHA-256 {a.pdf_sha256}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-amber-700">Nenhuma assinatura registrada.</p>
                    )}
                    <div className="grid gap-2 md:grid-cols-3">
                      <input
                        className="form-input"
                        placeholder="Nome do signatário"
                        value={signatario}
                        onChange={(e) => setSignatario(e.target.value)}
                      />
                      <input
                        className="form-input"
                        placeholder="CPF (opcional)"
                        value={signatarioCpf}
                        onChange={(e) => setSignatarioCpf(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={assinando}
                        onClick={async () => {
                          if (!signatario.trim()) {
                            toast.error('Informe o nome do signatário.');
                            return;
                          }
                          setAssinando(true);
                          try {
                            await licenseService.assinarLicenca(editingItem.id, {
                              signatario: signatario.trim(),
                              cpf: signatarioCpf.trim() || null,
                              tipo: 'registro',
                            });
                            toast.success('Assinatura registrada.');
                            setSignatario('');
                            setSignatarioCpf('');
                            await loadAssinaturas(editingItem.id);
                          } catch (err) {
                            toast.error(userFacingError(err, 'requestError'));
                          } finally {
                            setAssinando(false);
                          }
                        }}
                      >
                        {assinando ? 'Assinando…' : 'Assinar PDF'}
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="form-group sm:col-span-2">
                  <EntityAutocomplete
                    id="lic-emp"
                    label="Empreendimento (opcional)"
                    value={form.empreendimento_id}
                    onChange={(id) => setForm((f) => ({ ...f, empreendimento_id: id }))}
                    options={enterpriseOptions}
                    placeholder="Buscar empreendimento pelo nome…"
                    onAddNew={canAddEmpreendimento ? () => setChildModal('empreendimento') : undefined}
                    addNewLabel="Novo empreendimento…"
                  />
                </div>
              </div>
            </FormSection>
            <FormSection
              id="lic-sec-cond"
              title="Condicionantes"
              description="Obrigações da licença. Inclua manualmente ou importe do PDF. Anexe comprovantes por condicionante."
              icon={ClipboardList}
              variant="primary"
            >
              <CondicionantesEditor
                value={condicionantesDraft}
                onChange={setCondicionantesDraft}
                licencaId={editingItem?.id || null}
              />
            </FormSection>
            <FormSection
              id="lic-sec-checklist"
              title="Checklist do tipo"
              description="Itens do tipo de licença. Marque o cumprimento e anexe evidência."
              icon={ClipboardList}
            >
              <ChecklistEditor
                value={checklistDraft}
                onChange={setChecklistDraft}
                licencaId={editingItem?.id || null}
              />
            </FormSection>
          </form>
        </FormModalLayout>
      )}

      {showModal && childModal === 'tipo' && (
        <FormModalLayout
          zIndexClass={LICENSE_NESTED_MODAL_Z}
          title="Novo tipo de licença"
          subtitle="O tipo criado será selecionado no formulário da licença."
          headerIcon={Tags}
          onClose={() => {
            if (!savingNestedTipo) setChildModal(null);
          }}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setChildModal(null)}
                className="btn btn-outline"
                disabled={savingNestedTipo}
              >
                Cancelar
              </button>
              <Can any={[P.LICENSE_TIPOS, P.LICENSE_LICENCAS]}>
                <button
                  type="submit"
                  form="licenca-nested-tipo-form"
                  className="btn btn-primary"
                  disabled={savingNestedTipo}
                >
                  {savingNestedTipo ? 'Salvando…' : 'Criar e selecionar'}
                </button>
              </Can>
            </>
          }
        >
          <form id="licenca-nested-tipo-form" onSubmit={handleNestedTipoSave} className="space-y-4">
            <div className="form-group">
              <label htmlFor="lic-nested-tipo-nome" className="form-label form-label-required">
                Nome
              </label>
              <input
                id="lic-nested-tipo-nome"
                type="text"
                value={nestedTipoNome}
                onChange={(e) => setNestedTipoNome(e.target.value)}
                className="form-input"
                placeholder="Ex.: Licença de instalação"
                autoFocus
              />
            </div>
          </form>
        </FormModalLayout>
      )}

      {showModal && childModal === 'processo' && (
        <ProcessoNestedCreateModal
          zIndexClass={LICENSE_NESTED_MODAL_Z}
          onClose={() => setChildModal(null)}
          onCreated={(created) => {
            if (created?.id != null) {
              setForm((f) => ({ ...f, processo_id: created.id }));
            }
            setChildModal(null);
          }}
        />
      )}

      {showModal && childModal === 'empreendimento' && (
        <EnterpriseModal
          zIndexClass={LICENSE_NESTED_MODAL_Z}
          enterprise={null}
          onClose={() => setChildModal(null)}
          onSubmit={handleNestedEnterpriseSave}
        />
      )}

      {showModal && childModal === 'cliente' && (
        <ClientModal
          zIndexClass={LICENSE_NESTED_MODAL_Z}
          client={null}
          onClose={() => setChildModal(null)}
          onSubmit={handleNestedClientSave}
        />
      )}
    </>
  );
});

export default LicencasForm;
